// 🔑 THE STAFF QUEUES, TO THE APPROVED MOCKUPS (Jesse, 2026-10-26 — the approved Underwriting Module redesign mockups).
//   WaitingOnStaffQueue — the top of the Underwriting Modules page: every borrower blocked on a staff action
//     (backend utils/waitingOnStaff): borrower, step, what it waits on, how long, and a button straight to the Module
//     step. Urgent first (fraud holds, failed checks), then oldest first. Above the rows: the regulated mailing tasks
//     and the refused Reg B notices (moved from the retired Section Review page, 2026-10-28/29).
//   FraudReviewQueue — every open fraud flag across borrowers (backend utils/fraudFlagActions.fraudQueue): severity,
//     borrower, the flag in plain words, what it blocks, age, the document, and the same actions as the Module.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { T, card, btn, IdCheckEvidence } from './GuidedModuleSteps';
import { API } from './parts';
const MODULE_LABEL = { identity: 'Identity', income: 'Income', assets: 'Assets', housing: 'Housing', credit: 'Credit', misc: 'Docs', documents: 'Docs', decision: 'Decision', urla: '1003 and summary' };
const MODULE_STEP = (m) => (m === 'misc' ? 'documents' : m);
const age = (t) => {
  if (!t) return '—';
  const ms = Date.now() - new Date(t).getTime();
  if (!(ms >= 0)) return '—';
  const h = Math.floor(ms / 3600000); const d = Math.floor(h / 24);
  return d >= 1 ? `${d} d ${h % 24} h` : `${h} h ${Math.floor((ms % 3600000) / 60000)} m`;
};
const headersOf = (token) => ({ Authorization: `Bearer ${token}` });
export const moduleHref = (userId, module) => `/admin/borrowers/${userId}?tab=review&module=${encodeURIComponent(MODULE_STEP(module))}`;

// 🚨 THE TWO REGULATED QUEUES (Jesse, 2026-10-28/29 — moved here from the retired Section Review page): regulated
// disclosures that could not be delivered electronically and must be MAILED (download the copy, record the date — that
// date is the delivery; backend utils/regulatedMailing), and Reg B notices refused or never sent (utils/adverseNoticeRefusal).
export function RegulatedMailingTasks({ token, tasks }) {
  const [list, setList] = useState(tasks || []);
  const [mailedOn, setMailedOn] = useState({});
  const [msg, setMsg] = useState({});
  useEffect(() => { setList(tasks || []); }, [tasks]);
  if (!list.length) return null;
  const download = async (t) => {
    try {
      const r = await axios.get(`${API}/api/admin/regulated-mailing/${t.id}/pdf`, { headers: headersOf(token), responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a');
      a.href = url; a.download = `${t.kind}_${t.userId}_to_mail.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { setMsg((m) => ({ ...m, [t.id]: 'The copy could not be downloaded just now.' })); }
  };
  const record = async (t) => {
    try {
      const r = await axios.post(`${API}/api/admin/regulated-mailing/${t.id}/mailed`, { mailed_on: mailedOn[t.id] }, { headers: headersOf(token) });
      setMsg((m) => ({ ...m, [t.id]: r.data.late ? 'Recorded — after the mail-by date.' : 'Recorded.' }));
      setList((l) => l.filter((x) => x.id !== t.id));
    } catch (e) { setMsg((m) => ({ ...m, [t.id]: e?.response?.data?.error || 'Could not record.' })); }
  };
  return (
    <div data-testid="mailing-tasks" style={{ border: `1px solid ${T.line}`, borderLeft: `5px solid ${T.red}`, borderRadius: 12, padding: '12px 16px' }}>
      <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 6 }}>To mail · {list.length} — these could not be delivered electronically</div>
      {list.map((t) => (
        <div key={t.id} data-testid="mailing-task" style={{ fontSize: 13.5, padding: '8px 0', borderTop: `1px solid ${T.line}` }}>
          <div style={{ fontWeight: 700 }}>{t.title}</div>
          <div style={{ fontSize: 12.5 }}>
            {t.rule}{t.mailRule ? ` · ${t.mailRule}` : ''}{t.deadline ? ` · deadline ${String(t.deadline).slice(0, 10)}` : ''}
            {' · '}why: {t.reason}{t.attempts > 1 ? ` · blocked ${t.attempts}×` : ''}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
            {t.hasPdf
              ? <button type="button" onClick={() => download(t)} style={{ ...btn(), minHeight: 36 }}>Download the copy to mail</button>
              : <span style={{ fontSize: 12.5 }}>{t.pdfNote}</span>}
            <input type="date" value={mailedOn[t.id] || ''} onChange={(e) => setMailedOn({ ...mailedOn, [t.id]: e.target.value })}
              style={{ fontSize: 13, minHeight: 36 }} aria-label="Date mailed" />
            <button type="button" data-testid="mailing-record" disabled={!mailedOn[t.id]} onClick={() => record(t)} style={{ ...btn('primary'), minHeight: 36 }}>Record mailing date</button>
            {msg[t.id] && <span style={{ fontSize: 12.5 }}>{msg[t.id]}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

export function NoticeRefusals({ refusals, navigate }) {
  if (!(refusals || []).length) return null;
  return (
    <div data-testid="notice-refusals" style={{ border: `1px solid ${T.line}`, borderLeft: `5px solid ${T.red}`, borderRadius: 12, padding: '12px 16px' }}>
      <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 6 }}>Notices NOT sent · {refusals.length} — resolve before the Reg B due date</div>
      {refusals.map((r) => (
        <div key={r.id} data-testid="notice-refusal" style={{ fontSize: 13.5, padding: '8px 0', borderTop: `1px solid ${T.line}` }}>
          <button type="button" onClick={() => navigate(`/admin/borrowers/${r.userId}`)}
            style={{ background: 'none', border: 'none', padding: 0, font: `700 13.5px ${T.font}`, color: T.navy, textDecoration: 'underline', cursor: 'pointer' }}>{r.borrowerName}</button>
          {' '}· file #{r.userId} · {r.kind === 'counteroffer' ? 'counteroffer' : 'adverse-action'} notice
          {' '}· Reg B due <b>{r.regBDueDate ? String(r.regBDueDate).slice(0, 10) : 'unknown'}</b>
          <div style={{ fontSize: 12.5, marginTop: 2 }}>{r.reason}</div>
        </div>
      ))}
    </div>
  );
}

export function WaitingOnStaffQueue({ token, navigate }) {
  const [rows, setRows] = useState(null);
  const [mailing, setMailing] = useState([]);
  const [refusals, setRefusals] = useState([]);
  const [err, setErr] = useState(null);
  useEffect(() => {
    const keep = (d) => { setMailing((d && d.mailingTasks) || []); setRefusals((d && d.noticeRefusals) || []); };
    axios.get(`${API}/api/admin/modules/underwriting/waiting-on-staff`, { headers: headersOf(token) })
      .then((r) => { setRows(r.data.rows || []); keep(r.data); })
      .catch((e) => { setErr('The queue could not be read just now.'); setRows([]); keep(e?.response?.data); });
  }, [token]);
  return (
    <div data-testid="uw-waiting-on-staff" style={{ ...card({ borderTop: `5px solid ${T.yellow}`, gap: 14, marginBottom: 20 }), fontFamily: T.font, color: T.ink }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>Waiting on staff · {rows ? rows.length : '…'}</h2>
        <div style={{ fontSize: 13 }}>Urgent first, then oldest first</div>
      </div>
      {err && <div role="alert" style={{ fontSize: 13, fontWeight: 700 }}>{err}</div>}
      <RegulatedMailingTasks token={token} tasks={mailing} />
      <NoticeRefusals refusals={refusals} navigate={navigate} />
      {rows && !rows.length && !err && <div data-testid="uw-waiting-none" style={{ fontSize: 14 }}>No borrower is waiting on staff.</div>}
      {rows && rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 860 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: `2px solid ${T.ink}` }}>
                <th scope="col" style={{ padding: '10px 8px' }}>Borrower</th>
                <th scope="col" style={{ padding: '10px 8px' }}>Step</th>
                <th scope="col" style={{ padding: '10px 8px' }}>Waiting on</th>
                <th scope="col" style={{ padding: '10px 8px' }}>Waiting</th>
                <th scope="col" style={{ padding: '10px 8px' }}><span style={{ position: 'absolute', left: -9999 }}>Action</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.userId} data-testid="uw-waiting-row" data-user={r.userId} data-urgent={r.urgent ? 'true' : 'false'} style={{ borderBottom: `1px solid ${T.line}` }}>
                  <td style={{ padding: '12px 8px' }}><b>{r.name}</b>{r.loanNumber ? <div style={{ fontSize: 12 }}>{r.loanNumber}</div> : null}</td>
                  <td style={{ padding: '12px 8px' }}>{r.step != null ? r.step : '—'}</td>
                  <td data-testid="uw-waiting-on" style={{ padding: '12px 8px' }}>
                    {r.urgent && <span style={{ display: 'inline-flex', alignItems: 'center', minHeight: 22, padding: '0 8px', borderRadius: 11, background: T.red, color: T.white, fontSize: 11, fontWeight: 800, marginRight: 6 }}>URGENT</span>}
                    {r.waitingOn}
                  </td>
                  <td style={{ padding: '12px 8px' }}>{age(r.since)}</td>
                  <td style={{ padding: '12px 8px' }}>
                    <button type="button" data-testid="uw-waiting-open" data-module={r.module} onClick={() => navigate(moduleHref(r.userId, r.module))}
                      style={{ ...btn('primary'), minHeight: 40 }}>Open {MODULE_LABEL[r.module] || 'the Module'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const SEV = { high: { bg: T.red, fg: T.white, label: 'HIGH' }, medium: { bg: T.tan, fg: T.ink, label: 'MEDIUM' }, low: { bg: T.slateLight, fg: T.ink, label: 'LOW' } };
const DISMISS_REASONS = [['legitimate_explanation', 'There is a legitimate explanation'], ['borrower_provided_explanation', 'The borrower explained it'],
  ['verified_accurate', 'Verified as accurate'], ['duplicate_flag', 'Duplicate flag'], ['other', 'Other']];

export function FraudReviewQueue({ token, navigate }) {
  const [flags, setFlags] = useState(null);
  const [open, setOpen] = useState({});
  const [err, setErr] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/api/admin/modules/fraud-review`, { headers: headersOf(token) })
      .then((r) => setFlags(r.data.flags || [])).catch(() => { setErr('The queue could not be read just now.'); setFlags([]); });
  }, [token]);
  useEffect(() => { load(); }, [load]);
  const act = async (f, path, body) => {
    setErr(null);
    try { await axios.post(`${API}/api/admin/borrowers/${f.userId}/fraud-flags/${f.id}/${path}`, body || {}, { headers: headersOf(token) }); setOpen((o) => ({ ...o, [f.id]: null })); load(); }
    catch (e) { setErr(e?.response?.data?.error || 'That could not be saved just now.'); }
  };
  return (
    <div data-testid="uw-fraud-queue" style={{ fontFamily: T.font, color: T.ink, display: 'grid', gap: 14 }}>
      <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800 }}>Fraud review · {flags ? flags.length : '…'} open</h1>
      {err && <div role="alert" style={{ fontSize: 13, fontWeight: 700 }}>{err}</div>}
      {flags && !flags.length && !err && <div style={{ fontSize: 14 }}>No open fraud flags.</div>}
      {(flags || []).map((f) => {
        const s = SEV[f.severity] || SEV.low;
        const o = open[f.id] || null;
        return (
          <div key={f.id} data-testid="uw-fraud-queue-flag" data-severity={f.severity} data-test-account={f.testAccount ? 'true' : 'false'}
            data-reraises={f.reraisesFlagId || ''} style={card({ gap: 10 })}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', minHeight: 26, padding: '0 10px', borderRadius: 13, background: s.bg, color: s.fg, fontSize: 12, fontWeight: 800, border: f.severity === 'high' ? 'none' : `1px solid ${T.ink}` }}>{s.label}</span>
                {f.testAccount && <span data-testid="uw-fraud-queue-test-account" style={{ display: 'inline-flex', alignItems: 'center', minHeight: 26, padding: '0 10px', borderRadius: 13, background: T.slateLight, color: T.ink, fontSize: 12, fontWeight: 800, border: `1px solid ${T.ink}` }}>TEST ACCOUNT</span>}
                <span style={{ fontSize: 16, fontWeight: 800 }}>{f.title}</span>
                <span style={{ fontSize: 14 }}>· {f.borrower.name}{f.borrower.loanNumber ? ` · ${f.borrower.loanNumber}` : ''}</span>
              </div>
              <span style={{ fontSize: 13 }}>open {age(f.createdAt)}</span>
            </div>
            {f.reraisedLine && <div data-testid="uw-fraud-queue-reraised" style={{ fontSize: 13, fontWeight: 700 }}>{f.reraisedLine}</div>}
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>{f.found} <b>Blocks:</b> {f.holds}.</div>
            {f.document && (f.document.name || f.document.typeLabel) && (
              <div style={{ fontSize: 13, background: T.ground, borderRadius: 8, padding: '10px 12px' }}>{[f.document.name, f.document.typeLabel, f.document.uploadedAt ? `uploaded ${new Date(f.document.uploadedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : null].filter(Boolean).join(' · ')}</div>
            )}
            <IdCheckEvidence API={API} flag={f} headers={headersOf(token)} />
            {!o && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <button type="button" data-testid="uw-fraud-queue-dismiss" onClick={() => setOpen((x) => ({ ...x, [f.id]: { kind: 'dismiss', reason: '', notes: '' } }))} style={btn()}>Dismiss with a reason</button>
                {!f.idCheck && <button type="button" data-testid="uw-fraud-queue-keep" onClick={() => act(f, 'keep-request', {})} style={btn('primary')}>Keep and request a document</button>}
                {/* 🔑 AN ID FLAG'S OTHER TWO DECISIONS (Jesse, 2026-10-28): a new ID (the automatic retry, step 4 reopened) or
                    the normal decline (the adverse action notice). Both close every ID flag on the file. */}
                {f.idCheck && <button type="button" data-testid="uw-fraud-queue-new-id" onClick={() => setOpen((x) => ({ ...x, [f.id]: { kind: 'new-id', notes: '' } }))} style={btn()}>Ask for a new ID</button>}
                {f.idCheck && <button type="button" data-testid="uw-fraud-queue-decline" onClick={() => setOpen((x) => ({ ...x, [f.id]: { kind: 'decline-file', notes: '' } }))} style={btn()}>Decline the file</button>}
                <button type="button" data-testid="uw-fraud-queue-escalate" onClick={() => setOpen((x) => ({ ...x, [f.id]: { kind: 'escalate', notes: '' } }))} style={btn()}>Escalate</button>
                <button type="button" data-testid="uw-fraud-queue-open" onClick={() => navigate(moduleHref(f.userId, f.module))}
                  style={{ minHeight: 44, padding: '0 12px', background: 'none', border: 'none', font: `700 14px ${T.font}`, color: T.ink, textDecoration: 'underline', cursor: 'pointer' }}>Open in {f.moduleLabel}</button>
              </div>
            )}
            {o && o.kind === 'dismiss' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <select aria-label="Reason" value={o.reason} onChange={(e) => setOpen((x) => ({ ...x, [f.id]: { ...o, reason: e.target.value } }))}
                  style={{ minHeight: 40, borderRadius: 8, border: `1px solid ${T.ink}`, font: `500 14px ${T.font}` }}>
                  <option value="">Choose a reason…</option>
                  {DISMISS_REASONS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                </select>
                <input aria-label="Notes" placeholder="Notes (recorded on the file)" value={o.notes} onChange={(e) => setOpen((x) => ({ ...x, [f.id]: { ...o, notes: e.target.value } }))}
                  style={{ minHeight: 40, flex: '1 1 240px', borderRadius: 8, border: `1px solid ${T.ink}`, padding: '0 10px', font: `500 14px ${T.font}` }} />
                <button type="button" disabled={!o.reason} onClick={() => act(f, 'dismiss', { reason: o.reason, notes: o.notes })} style={btn('primary', !o.reason)}>Dismiss</button>
                <button type="button" onClick={() => setOpen((x) => ({ ...x, [f.id]: null }))} style={btn()}>Cancel</button>
              </div>
            )}
            {o && (o.kind === 'new-id' || o.kind === 'decline-file') && (
              <div data-testid={`uw-fraud-queue-${o.kind}-confirm`} style={{ display: 'grid', gap: 8 }}>
                <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                  {o.kind === 'new-id'
                    ? <>Step 4 reopens for the borrower with the automatic check&rsquo;s retry, and one alert: &ldquo;We couldn&rsquo;t verify your ID. Please try again with a clear photo of a valid, unexpired ID.&rdquo; Your note stays on the file.</>
                    : <>Declines the file through the adverse action: each applicant gets the notice (reason: &ldquo;Unable to verify identity&rdquo;) in Documents and by email, and step 8 shows the decision. Your note stays on the file.</>}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <input aria-label="Notes" placeholder="Notes (recorded on the file, never shown to the borrower)" value={o.notes} onChange={(e) => setOpen((x) => ({ ...x, [f.id]: { ...o, notes: e.target.value } }))}
                    style={{ minHeight: 40, flex: '1 1 280px', borderRadius: 8, border: `1px solid ${T.ink}`, padding: '0 10px', font: `500 14px ${T.font}` }} />
                  <button type="button" data-testid={`uw-fraud-queue-${o.kind}-go`} onClick={() => act(f, o.kind, { note: o.notes })} style={btn('primary')}>{o.kind === 'new-id' ? 'Ask for a new ID' : 'Decline the file'}</button>
                  <button type="button" onClick={() => setOpen((x) => ({ ...x, [f.id]: null }))} style={btn()}>Cancel</button>
                </div>
              </div>
            )}
            {o && o.kind === 'escalate' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <input aria-label="What to look at" placeholder="What should they look at?" value={o.notes} onChange={(e) => setOpen((x) => ({ ...x, [f.id]: { ...o, notes: e.target.value } }))}
                  style={{ minHeight: 40, flex: '1 1 280px', borderRadius: 8, border: `1px solid ${T.ink}`, padding: '0 10px', font: `500 14px ${T.font}` }} />
                <button type="button" onClick={() => act(f, 'escalate', { note: o.notes })} style={btn('primary')}>Escalate</button>
                <button type="button" onClick={() => setOpen((x) => ({ ...x, [f.id]: null }))} style={btn()}>Cancel</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
